import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import { Header } from "./components/Header";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

const API_URL = "/api";

export default function RepaymentAndInterest() {
  const { id } = useParams();
  console.log("🔎 Route id:", id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [company, setCompany] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState([]);
  const [startDate, setStartDate] = useState("");

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getTransactionDetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        console.log("📥 Full API Response:", data);
        setTransactionData(data);
        console.log("transactionData:", transactionData);
        // setFormData(data?.DocumentCheckList || {});
        setFormData(data); // keep full object for Loan Info
        setCompany(data);
        console.log("Data:", data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, []);

  // helper: last day of a month
  const getLastDayOfMonth = (date) => {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  };
  // --- PMT, IPMT, PPMT helpers ---
  // put these above your component
  // Excel-compatible PMT
  function PMT(rate, nper, pv, fv = 0, type = 0) {
    if (rate === 0) return -(pv + fv) / nper;
    const pvif = Math.pow(1 + rate, nper);
    return (-rate * (pv * pvif + fv)) / ((1 + rate * type) * (pvif - 1));
  }

  // Excel-compatible IPMT
  function IPMT(rate, per, nper, pv, fv = 0, type = 0) {
    const pmt = PMT(rate, nper, pv, fv, type);
    let ipmt;
    if (per === 1) {
      if (type === 1) {
        ipmt = 0;
      } else {
        ipmt = -pv * rate;
      }
    } else {
      let fvPer = PMT(rate, nper, pv, fv, type) * (1 + rate * type);
      let tmp = Math.pow(1 + rate, per - 1);
      let balance = pv * tmp + (fvPer * (tmp - 1)) / rate;
      ipmt = balance * rate;
    }
    return ipmt;
  }

  // Excel-compatible PPMT
  function PPMT(rate, per, nper, pv, fv = 0, type = 0) {
    return PMT(rate, nper, pv, fv, type) - IPMT(rate, per, nper, pv, fv, type);
  }

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData(transactionData);
    setChecklistFormData(transactionData?.DocumentCheckList || []);
    setIsEditing(false);
  };

  const renderStatusCell = (section) => {
    let value = formData?.[section]?.STATUS || "";
    if (section === "ENTEREXPIRYDATE") value = "";
    return isEditing ? (
      <select
        className="form-select"
        value={value}
        onChange={(e) => handleChange(e, section, "STATUS")}
      >
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="N/A">N/A</option>
      </select>
    ) : (
      <div
        style={{
          backgroundColor: value === "Yes" ? "#58a65c" : "#fdf3d0",
          color: value === "Yes" ? "#fff" : "#000",
          textAlign: "center",
        }}
      >
        {value || ""}
      </div>
    );
  };

  const criteriaLabels = {
    COMPANYNAMEISCORRECT: "Company name is correct",
    THEREGISTRATIONISCURRENT: "The registration is current",
    THERETURNCOVERSTHEMOSTRECENTPERIOD:
      "The return covers the most recent period",
    SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED:
      "Shareholder(s) match organization chart (if provided)",
    THECHARTISSIGNEDANDDATED: "The chart is signed and dated",
    STATEMENTSARESIGNEDBYAUDITOR: "Statements are signed by auditor",
  };

  const renderCheckCell = (section) => {
    // if (section === "SUBMITTEDBUSINESSREGISTRATION") return "";

    // Show the label for these fields instead of CHECKINGDETAILS
    if (
      section === "COMPANYNAMEISCORRECT" ||
      section === "THEREGISTRATIONISCURRENT" ||
      section === "THERETURNCOVERSTHEMOSTRECENTPERIOD" ||
      section === "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED" ||
      section === "THECHARTISSIGNEDANDDATED" ||
      section === "STATEMENTSARESIGNEDBYAUDITOR"
    ) {
      return criteriaLabels[section] || "";
    }

    if (section === "ENTEREXPIRYDATE") {
      const rawDate = formData?.ENTEREXPIRYDATE?.CHECKINGDETAILS;
      return rawDate ? formatDate(rawDate) : "";
    }

    return formData?.[section]?.CHECKINGDETAILS || "";
  };

  const handleInputChange = (sectionKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { _id, ...rest } = formData;
    if (!_id) {
      alert("Missing _id");
      return;
    }
    // Remove any fields with undefined values
    const cleanedData = {};
    for (const key in rest) {
      if (rest[key] !== undefined) {
        cleanedData[key] = rest[key];
      }
    }
    const updatePayload = {
      _id: _id, // Make sure it's a string
      update_data: cleanedData,
    };

    try {
      const response = await fetch(
        "http://localhost:8000/riverchain/techop/v1.1/updateCompanyLog",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      const resultText = await response.text();

      if (!response.ok) {
        console.error("❌ Update failed:", resultText);
        alert("Update failed:\n" + resultText);
      } else {
        toast.success("Company log updated successfully");
      }
    } catch (error) {
      console.error("🚨 Network error:", error);
      toast.danger("An error occurred while updating the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };
  // Utility: format date values into DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString; // fallback if invalid date
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };
  const scoreMapping = {
    "Borrower: A": 20,
    "Borrower: B": 15,
    "Borrower: C": 0,
    "Borrower: D": -10,
  };
  const scoreMappingTransaction = {
    "0% to 10%": 0,
    "11% to 20%": 5,
    "21% to 30%": 8,
    "> 30%": 10,
  };
  const today = new Date();
  const formatted = today.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long", // 👈 gives full month name
    year: "numeric",
  });

  const PaymentSummary = [
    {
      "Payment #": 1,
      Loan: "Tranche 1",
      "Payment Date": "",
      "Payment Amount": "",
      "Admin Fee Paid":
        (transactionData?.TRANCHE1 ?? 0) *
        (transactionData?.PROPOSED_TRANS_TERM?.ADMINFEE ?? 0) *
        100,
      "Interest Period Start Date": "",
      "Interest Paid": "",
      "Principal Repayment": "",
      "Loan Balance": "",
    },
    {
      "Payment #": 1,
      Loan: "Tranche 1",
      "Payment Date": "",
      "Payment Amount": "",
      "Admin Fee Paid":
        (transactionData?.TRANCHE1 ?? 0) *
        (transactionData?.PROPOSED_TRANS_TERM?.ADMINFEE ?? 0) *
        100,
      "Interest Period Start Date": "",
      "Interest Paid": "",
      "Principal Repayment": "",
      "Loan Balance": "",
    },
  ];
  const enrichedPaymentSummary = PaymentSummary.map((row) => ({ ...row }));
  // Now safely calculate sums

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <Header />
        </main>
      </div>

      <div className="container">
        <div className="row mt-4">
          <div className="col-md-12">
            <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
              Back to Transaction List
            </NavLink>

            <br />
            <br />
            <NavLink
              to={`/ViewTransactionList/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Loan Details
            </NavLink>

            <NavLink
              to={`/Drawdowns/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Drawdowns
            </NavLink>

            <NavLink
              to={`/ApplicationChecklist/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Application Checklist
            </NavLink>

            <NavLink
              to={`/ScoreCard/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scorecard
            </NavLink>

            <NavLink
              to={`/TransactionSummary/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Transaction Summary
            </NavLink>

            <NavLink
              to={`/TermSheet/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Term Sheet
            </NavLink>

            <NavLink
              to={`/Payments/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Payments
            </NavLink>
            <NavLink
              to={`/RepaymentAndInterest/${id}`}
              className="btn btn-dark ms-2"
            >
              Repayment & Interest(Term)
            </NavLink>

            <NavLink
              to={`/ScoringCriteriaTf/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Criteria
            </NavLink>

            <NavLink
              to={`/ScoringGuide/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Guide
            </NavLink>

            <NavLink
              to={`/InterestCalculations/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Interest Calculations
            </NavLink>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginTop: "15px" }}>Repayment & Interest(Term)</h3>
              <div>
                {isEditing && (
                  <button
                    className="btn btn-secondary me-2 btn-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      setIsSubmitting(true);
                      await handleSubmit();
                      setIsSubmitting(false);
                    } else {
                      setFormData(transactionData);
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditing
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Edit"}
                </button>
              </div>
            </div>

            {loading || !transactionData ? (
              <ShimmerTable row={5} col={5} />
            ) : (
              <>
                <div className="col-md-12">
                  <table>
                    <tbody>
                      <tr>
                        <td>{transactionData?.BORROWER_LABEL || "BORROWER"}</td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.BORROWER || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {transactionData?.TRANSACTIONID_LABEL ||
                            "Transaction ID"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.TRANSACTIONID || "N/A"}
                        </td>
                      </tr>
                      <br></br>
                      <tr>
                        <td>
                          {transactionData?.LOANAMOUNT_HK_LABEL ||
                            "Loan Amount"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.LOANAMOUNT_HK || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>{transactionData?.TRANCHE1_LABEL || "TRANCHE1"}</td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.TRANCHE1 || "0"}
                        </td>
                      </tr>
                      <tr>
                        <td>{transactionData?.TRANCHE2_LABEL || "TRANCHE2"}</td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.TRANCHE2 || "0"}
                        </td>
                      </tr>
                      <br></br>
                      <tr>
                        <td>
                          {transactionData?.MAXTERM_MONTHS_LABEL ||
                            "Term(Months)"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.MAXTERM_MONTHS || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {transactionData?.INTERESTRATE_LABEL ||
                            "Interest Rate(p.a)"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.INTERESTRATE
                            ? (transactionData?.INTERESTRATE * 100).toFixed(2) +
                              "%"
                            : "N/A"}
                        </td>
                      </tr>
                      <br></br>
                      <tr>
                        <td>
                          {transactionData?.ADMINFEE_LABEL || "Admin Fee"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.ADMINFEE
                            ? (transactionData?.ADMINFEE * 100).toFixed(2) + "%"
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <br></br>
                  <table>
                    <thead>
                      <tr
                        style={{
                          borderBlock: "2px solid black",
                          textAlign: "center",
                        }}
                      >
                        <th colSpan={"6"} style={{ backgroundColor: "yellow" }}>
                          Repayment Schedule
                        </th>
                      </tr>
                      <tr style={{ borderBlock: "2px solid black" }}>
                        <th>Month</th>
                        <th style={{ paddingLeft: "40px" }}>
                          Principal Repayment
                        </th>
                        <th style={{ paddingLeft: "40px" }}>
                          Interest Payment
                        </th>
                        <th style={{ paddingLeft: "40px" }}>
                          Total Loan Payment
                        </th>
                        <th style={{ paddingLeft: "40px" }}>Fees</th>
                        <th style={{ paddingLeft: "40px" }}>Total Payments</th>
                      </tr>
                    </thead>

                    {(() => {
                      const loanAmount = Number(
                        transactionData?.LOANAMOUNT_HK || 0
                      );
                      const annualRate = Number(
                        transactionData?.INTERESTRATE || 0
                      );
                      const months = Math.max(
                        0,
                        Number(transactionData?.MAXTERM_MONTHS || 0)
                      );
                      const adminFeeRate = Number(
                        transactionData?.ADMINFEE || 0
                      );

                      const monthlyRate = annualRate / 12;
                      const maxMonths = Math.min(months || 12, 12);

                      let monthlyPayment;
                      if (monthlyRate === 0) {
                        monthlyPayment = loanAmount / months;
                      } else {
                        const tmp = Math.pow(1 + monthlyRate, months);
                        monthlyPayment =
                          (loanAmount * monthlyRate * tmp) / (tmp - 1);
                      }

                      let balance = loanAmount;

                      // totals
                      let totalPrincipal = 0;
                      let totalInterest = 0;
                      let totalLoanPayment = 0;
                      let totalFees = 0;
                      let totalPayments = 0;

                      const rows = [];

                      for (let month = 1; month <= maxMonths; month++) {
                        let interest = balance * monthlyRate;
                        let principal = monthlyPayment - interest;

                        if (month === months) {
                          principal = balance;
                          monthlyPayment = principal + interest;
                        }
                        if (principal < 0) principal = 0;

                        const fee = month === 1 ? loanAmount * adminFeeRate : 0;
                        const loanPay = principal + interest;
                        const totalPay = loanPay + fee;

                        // accumulate totals
                        totalPrincipal += principal;
                        totalInterest += interest;
                        totalLoanPayment += loanPay;
                        totalFees += fee;
                        totalPayments += totalPay;

                        rows.push(
                          <tr key={month}>
                            <td>{month}</td>
                            <td>${principal.toFixed(2)}</td>
                            <td>${interest.toFixed(2)}</td>
                            <td>${loanPay.toFixed(2)}</td>
                            <td>${fee.toFixed(2)}</td>
                            <td>${totalPay.toFixed(2)}</td>
                          </tr>
                        );

                        balance = Math.max(0, balance - principal);
                      }

                      return (
                        <>
                          <tbody style={{ textAlign: "right" }}>{rows}</tbody>
                          <tfoot
                            style={{
                              borderTop: "2px solid black",
                              fontWeight: "bold",
                              textAlign: "right",
                            }}
                          >
                            <tr>
                              <td style={{ textAlign: "left" }}>Total</td>
                              <td>${totalPrincipal.toFixed(2)}</td>
                              <td>${totalInterest.toFixed(2)}</td>
                              <td>${totalLoanPayment.toFixed(2)}</td>
                              <td>${totalFees.toFixed(2)}</td>
                              <td>${totalPayments.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </>
                      );
                    })()}
                  </table>
                  <br></br>
                  <table>
                    <thead>
                      <tr style={{ borderBlock: "2px solid black" }}>
                        <th
                          colSpan={"12"}
                          style={{ backgroundColor: "lightblue" }}
                        >
                          Interest Calculations - Term Loan
                        </th>
                      </tr>
                      <tr style={{ borderBlock: "2px solid black" }}>
                        <th>Month</th>
                        <th style={{ paddingLeft: "40px" }}>Start Date</th>
                        <th style={{ paddingLeft: "40px" }}>End Date</th>
                        <th style={{ paddingLeft: "40px" }}>Days</th>
                        <th style={{ paddingLeft: "40px" }}>
                          Interest for the Period
                        </th>
                        <th style={{ paddingLeft: "40px" }}>
                          Fees for the Period
                        </th>
                        <th></th>
                        <th style={{ paddingLeft: "40px" }}>End of Month</th>
                        <th style={{ paddingLeft: "40px" }}>
                          Interest for the 1/2 month
                        </th>
                        <th style={{ paddingLeft: "40px" }}>
                          Interest for the 2/2 month
                        </th>
                        <th style={{ paddingLeft: "40px" }}>
                          Interest for the Month
                        </th>
                        <th style={{ paddingLeft: "40px" }}>
                          Fees for the Month
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ textAlign: "center" }}>
                      {(() => {
                        const loanAmount = Number(
                          transactionData?.LOANAMOUNT_HK || 0
                        );
                        const annualRate = Number(
                          transactionData?.INTERESTRATE || 0
                        );
                        const months = Number(
                          transactionData?.MAXTERM_MONTHS || 1
                        );
                        const adminFeeRate = Number(
                          transactionData?.ADMINFEE || 0
                        );

                        const monthlyRate = annualRate / 12;
                        let balance = loanAmount;

                        let totalDays = 0;
                        let totalInterest = 0;
                        let totalFees = 0;
                        const rows = [];

                        if (!startDate) {
                          // ✅ No start date → still show dynamic rows = months
                          for (let month = 1; month <= months; month++) {
                            const interestPeriod = balance * monthlyRate;
                            const fee =
                              month === 1 ? loanAmount * adminFeeRate : 0;

                            totalInterest += interestPeriod;
                            totalFees += fee;

                            rows.push(
                              <tr key={month}>
                                <td>{month}</td>
                                <td>
                                  {month === 1 ? (
                                    <input
                                      type="date"
                                      value={startDate}
                                      onChange={(e) =>
                                        setStartDate(e.target.value)
                                      }
                                    />
                                  ) : (
                                    ""
                                  )}
                                </td>
                                <td></td> {/* End Date blank */}
                                <td></td> {/* Days blank */}
                                <td>${interestPeriod.toFixed(2)}</td>{" "}
                                {/* Interest for Period */}
                                <td>${fee.toFixed(2)}</td>{" "}
                                {/* Fee for Period */}
                                <td></td>
                                <td></td> {/* End of Month blank */}
                                <td></td> {/* Half 1 blank */}
                                <td></td> {/* Half 2 blank */}
                                <td>${interestPeriod.toFixed(2)}</td>{" "}
                                {/* ✅ Interest for Month */}
                                <td>${fee.toFixed(2)}</td> {/* Fee for Month */}
                              </tr>
                            );

                            balance = Math.max(
                              0,
                              balance - loanAmount / months
                            );
                          }

                          // Totals row
                          rows.push(
                            <tr
                              key="total"
                              style={{
                                borderBlock: "2px solid black",
                                fontWeight: "bold",
                              }}
                            >
                              <td>Total</td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>${totalInterest.toFixed(2)}</td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>${totalInterest.toFixed(2)}</td>
                              <td>${totalFees.toFixed(2)}</td>
                            </tr>
                          );

                          return rows;
                        }

                        // ✅ If startDate exists → your existing split logic
                        let currentStart = new Date(startDate);

                        for (let month = 1; month <= months; month++) {
                          let start = new Date(currentStart);
                          let end = getLastDayOfMonth(start);

                          const days =
                            (Date.UTC(
                              end.getFullYear(),
                              end.getMonth(),
                              end.getDate()
                            ) -
                              Date.UTC(
                                start.getFullYear(),
                                start.getMonth(),
                                start.getDate()
                              )) /
                              (1000 * 60 * 60 * 24) +
                            1;

                          const interestPeriod = balance * monthlyRate;
                          const firstHalfDays = Math.floor(days / 2);
                          const secondHalfDays = days - firstHalfDays;
                          const firstHalfInterest =
                            (firstHalfDays / days) * interestPeriod;
                          const secondHalfInterest =
                            (secondHalfDays / days) * interestPeriod;

                          const fee =
                            month === 1 ? loanAmount * adminFeeRate : 0;

                          totalDays += days;
                          totalInterest += interestPeriod;
                          totalFees += fee;

                          rows.push(
                            <tr key={month}>
                              <td>{month}</td>
                              <td>
                                {month === 1 ? (
                                  <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) =>
                                      setStartDate(e.target.value)
                                    }
                                  />
                                ) : (
                                  start.toLocaleDateString("en-GB")
                                )}
                              </td>
                              <td>{end.toLocaleDateString("en-GB")}</td>
                              <td>{days}</td>
                              <td>${interestPeriod.toFixed(2)}</td>
                              <td>${fee.toFixed(2)}</td>
                              <td></td>
                              <td>{end.toLocaleDateString("en-GB")}</td>
                              <td>${firstHalfInterest.toFixed(2)}</td>
                              <td>${secondHalfInterest.toFixed(2)}</td>
                              <td>${interestPeriod.toFixed(2)}</td>
                              <td>${fee.toFixed(2)}</td>
                            </tr>
                          );

                          currentStart = new Date(end);
                          currentStart.setDate(currentStart.getDate() + 1);

                          balance = Math.max(0, balance - loanAmount / months);
                        }

                        rows.push(
                          <tr
                            key="total"
                            style={{
                              borderBlock: "2px solid black",
                              fontWeight: "bold",
                            }}
                          >
                            <td>Total</td>
                            <td></td>
                            <td></td>
                            <td>{totalDays}</td>
                            <td>${totalInterest.toFixed(2)}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>${totalInterest.toFixed(2)}</td>
                            <td>${totalFees.toFixed(2)}</td>
                          </tr>
                        );

                        return rows;
                      })()}
                    </tbody>
                  </table>
                  <br></br>
                  <br></br>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
